import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { useI18n } from "../../contexts/I18nContext";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../components/ui/dialog";
import { Wallet, Plus, QrCode, Shield, History, Sparkles, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import QRCode from "react-qr-code";
import { WalletTransactions } from "../../components/WalletTransactions";

const CATEGORIES = ["meals", "drinks", "juices", "fruits", "sweets", "snacks", "bakery"];

export default function CanteenWalletPage() {
  const { t } = useI18n();
  const [wallets, setWallets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [topUp, setTopUp] = useState(null);     // {student_id, name}
  const [topAmount, setTopAmount] = useState("");
  const [topGw, setTopGw] = useState("stripe");
  const [topBusy, setTopBusy] = useState(false);
  const [limits, setLimits] = useState(null);   // {student_id, daily_limit, blocked_categories[]}
  const [qrOf, setQrOf] = useState(null);
  const [historyOf, setHistoryOf] = useState(null);
  const [hist, setHist] = useState([]);
  const [mealPlanFor, setMealPlanFor] = useState(null);
  const [pubSettings, setPubSettings] = useState({ payment_provider: "none", paymob_configured: false });

  useEffect(() => {
    api.get("/settings/public").then(r => setPubSettings(r.data || {})).catch(() => {});
  }, []);

  // For parents the "cash/manual" gateway makes no sense — remove it. Admins still get it
  // via a separate admin adjustment endpoint (Canteen Admin page).
  const paymobAvailable = pubSettings.paymob_configured && pubSettings.payment_provider === "paymob";
  const gateways = paymobAvailable ? ["paymob"] : ["stripe", "mada"];

  // Sync the default selected gateway whenever the dialog is opened or config changes.
  // Radix Dialog's onOpenChange does NOT fire when `open` is toggled from an external
  // controlled prop, so we can't initialize inside onOpenChange (see iter30 RCA).
  useEffect(() => {
    if (topUp) {
      setTopGw(paymobAvailable ? "paymob" : "stripe");
      setTopAmount("");
    }
  }, [topUp, paymobAvailable]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/canteen/wallet/me");
      setWallets(data || []);
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const submitTopUp = async () => {
    const amt = parseFloat(topAmount);
    if (!amt || amt <= 0) { toast.error(t("invalid_amount")); return; }
    setTopBusy(true);
    try {
      if (topGw === "paymob") {
        // Real payment flow — create top-up + Paymob intention, redirect to checkout.
        const { data } = await api.post("/canteen/wallet/topup/paymob", {
          student_id: topUp.student_id, amount: amt, gateway: "paymob",
        });
        if (data?.checkout_url) {
          toast.success(t("redirecting_paymob") || "Redirecting to secure checkout…");
          window.location.href = data.checkout_url;
          return; // don't close the dialog until browser navigates
        }
        toast.error(t("paymob_no_checkout_url") || "Paymob did not return a checkout URL — check configuration");
      } else {
        const { data } = await api.post("/canteen/wallet/topup", {
          student_id: topUp.student_id, amount: amt, gateway: topGw,
        });
        if (topGw !== "manual") {
          // Mock: auto-confirm via webhook for demo Stripe/Mada
          await api.post("/canteen/wallet/topup/webhook", {
            gateway_ref: data.gateway_ref, gateway: topGw, success: true,
          });
        }
        toast.success(t("topup_success"));
        setTopUp(null); setTopAmount(""); load();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail?.paymob?.detail || e.response?.data?.detail || "Error");
    }
    setTopBusy(false);
  };

  const openLimits = (w) => {
    setLimits({
      student_id: w.student_id,
      student_name: w.student_name,
      daily_limit: w.limits.daily_limit || 30,
      blocked_categories: w.limits.blocked_categories || [],
    });
  };

  const saveLimits = async () => {
    try {
      await api.put(`/canteen/wallet/${limits.student_id}/limits`, {
        daily_limit: parseFloat(limits.daily_limit),
        blocked_categories: limits.blocked_categories,
      });
      toast.success(t("saved")); setLimits(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const toggleCat = (cat) => {
    setLimits((l) => ({
      ...l,
      blocked_categories: l.blocked_categories.includes(cat)
        ? l.blocked_categories.filter((c) => c !== cat)
        : [...l.blocked_categories, cat],
    }));
  };

  const loadHistory = async (w) => {
    setHistoryOf(w);
    try {
      const { data } = await api.get(`/canteen/wallet/${w.student_id}`);
      setHist(data.transactions || []);
    } catch (e) { toast.error("Error"); }
  };

  return (
    <div className="space-y-6 max-w-4xl mx-auto" data-testid="canteen-wallet-page">
      <div className="flex items-center gap-3">
        <Wallet className="w-8 h-8 text-primary" />
        <h1 className="text-2xl sm:text-3xl font-semibold">{t("canteen_wallet_title")}</h1>
      </div>

      {loading ? (
        <Card className="p-12 text-center text-muted-foreground">{t("loading")}</Card>
      ) : wallets.length === 0 ? (
        <Card className="p-12 text-center text-muted-foreground border-dashed">{t("no_children_yet")}</Card>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {wallets.map((w) => {
            const used = (w.limits.daily_limit || 30) - w.remaining_today;
            const pct = (used / (w.limits.daily_limit || 30)) * 100;
            return (
              <Card key={w.student_id} className="p-5 border shadow-none space-y-4" data-testid={`wallet-${w.student_id}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-semibold">{w.student_name}</h3>
                    <p className="text-xs text-muted-foreground mt-1">{t("daily_limit")}: {w.limits.daily_limit} {t("sar_unit")}</p>
                  </div>
                  <Button size="icon" variant="ghost" onClick={() => setQrOf(w)} data-testid={`qr-${w.student_id}`}>
                    <QrCode className="w-5 h-5" />
                  </Button>
                </div>

                <div className="bg-gradient-to-br from-primary/15 to-primary/5 rounded-lg p-4">
                  <div className="text-3xl font-bold">{w.balance.toFixed(2)} <span className="text-base font-normal text-muted-foreground">{t("sar_unit")}</span></div>
                  <div className="text-xs text-muted-foreground mt-1">{t("wallet_balance")}</div>
                </div>

                <div>
                  <div className="flex justify-between text-xs mb-1">
                    <span>{t("spent_today")}: {used.toFixed(2)}</span>
                    <span>{t("remaining")}: {w.remaining_today.toFixed(2)}</span>
                  </div>
                  <div className="h-2 bg-muted rounded-full overflow-hidden">
                    <div className={`h-full ${pct > 80 ? "bg-amber-500" : "bg-primary"}`} style={{ width: `${Math.min(100, pct)}%` }} />
                  </div>
                </div>

                <div className="grid grid-cols-4 gap-2">
                  <Button size="sm" onClick={() => setTopUp({ student_id: w.student_id, name: w.student_name })} data-testid={`topup-${w.student_id}`}>
                    <Plus className="w-4 h-4 me-1" /> {t("topup")}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => openLimits(w)} data-testid={`limits-${w.student_id}`}>
                    <Shield className="w-4 h-4 me-1" /> {t("controls")}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => loadHistory(w)} data-testid={`history-${w.student_id}`}>
                    <History className="w-4 h-4 me-1" /> {t("history")}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setMealPlanFor(w)} data-testid={`mealplan-${w.student_id}`}>
                    <Sparkles className="w-4 h-4 me-1" /> {t("meal_plan")}
                  </Button>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Recent wallet transactions — self-reconciles Paymob pendings on mount */}
      <WalletTransactions />

      <MealPlanDialog open={!!mealPlanFor} onClose={() => { setMealPlanFor(null); load(); }} wallet={mealPlanFor} t={t} />

      {/* Topup dialog */}
      <Dialog open={!!topUp} onOpenChange={(v) => { if (!v) setTopUp(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("topup_title")} — {topUp?.name}</DialogTitle>
            <DialogDescription>{t("topup_desc")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>{t("amount")}</Label>
              <Input type="number" value={topAmount} onChange={(e) => setTopAmount(e.target.value)} placeholder="100" data-testid="topup-amount" />
            </div>
            <div>
              <Label>{t("payment_method")}</Label>
              <div className={`grid ${gateways.length === 2 ? "grid-cols-2" : "grid-cols-3"} gap-2 mt-1`}>
                {gateways.map((gw) => (
                  <button key={gw} type="button" onClick={() => setTopGw(gw)}
                    className={`px-3 py-2 rounded-lg border text-sm font-medium smooth
                      ${topGw === gw ? "border-primary bg-primary/10 text-primary" : "border-border"}`}
                    data-testid={`gw-${gw}`}>
                    {t(`gw_${gw}`) || gw}
                  </button>
                ))}
              </div>
              {paymobAvailable && topGw === "paymob" && (
                <p className="text-xs text-muted-foreground mt-2" data-testid="paymob-hint">
                  {t("paymob_redirect_hint") || "سيتم تحويلك إلى صفحة الدفع الآمنة عبر Paymob لإتمام العملية بالبطاقة (Visa / Mada / MasterCard)."}
                </p>
              )}
            </div>
            <div className="flex gap-2 pt-2">
              <Button variant="outline" className="flex-1" onClick={() => setTopUp(null)}>{t("cancel")}</Button>
              <Button className="flex-1" onClick={submitTopUp} disabled={topBusy} data-testid="topup-confirm">
                {topBusy ? "..." : t("confirm_topup")}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Limits dialog */}
      <Dialog open={!!limits} onOpenChange={(v) => !v && setLimits(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("controls_title")} — {limits?.student_name}</DialogTitle>
          </DialogHeader>
          {limits && (
            <div className="space-y-4">
              <div>
                <Label>{t("daily_limit")} ({t("sar_unit")})</Label>
                <Input type="number" value={limits.daily_limit}
                  onChange={(e) => setLimits({ ...limits, daily_limit: e.target.value })}
                  data-testid="limit-amount" />
              </div>
              <div>
                <Label className="mb-2 block">{t("blocked_categories")}</Label>
                <div className="grid grid-cols-2 gap-2">
                  {CATEGORIES.map((cat) => (
                    <label key={cat} className="flex items-center gap-2 px-3 py-2 rounded-md border cursor-pointer smooth hover:bg-muted/50">
                      <input type="checkbox" checked={limits.blocked_categories.includes(cat)}
                        onChange={() => toggleCat(cat)}
                        className="w-4 h-4 accent-destructive"
                        data-testid={`cat-${cat}`} />
                      <span className="text-sm">{t(`cat_${cat}`)}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div className="flex gap-2 pt-2">
                <Button variant="outline" className="flex-1" onClick={() => setLimits(null)}>{t("cancel")}</Button>
                <Button className="flex-1" onClick={saveLimits} data-testid="save-limits">{t("save")}</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* QR dialog */}
      <Dialog open={!!qrOf} onOpenChange={(v) => !v && setQrOf(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>{qrOf?.student_name}</DialogTitle>
            <DialogDescription>{t("show_qr_at_canteen")}</DialogDescription>
          </DialogHeader>
          {qrOf && (
            <div className="flex flex-col items-center gap-3 py-3">
              <div className="bg-white p-4 rounded-lg">
                <QRCode value={qrOf.qr_token} size={220} />
              </div>
              <code className="text-xs text-muted-foreground font-mono">{qrOf.qr_token}</code>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* History */}
      <Dialog open={!!historyOf} onOpenChange={(v) => !v && setHistoryOf(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{t("transaction_history")} — {historyOf?.student_name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-1 max-h-96 overflow-y-auto">
            {hist.length === 0 ? (
              <div className="text-center py-8 text-sm text-muted-foreground">{t("no_data_yet")}</div>
            ) : (
              hist.map((tx) => (
                <div key={tx.id} className="flex items-center justify-between py-2 border-b border-border text-sm">
                  <div>
                    <div className="font-medium">{t(`txn_${tx.type}`)}</div>
                    <div className="text-xs text-muted-foreground">{new Date(tx.at).toLocaleString()}</div>
                  </div>
                  <div className={`font-mono ${tx.amount > 0 ? "text-emerald-600" : "text-red-600"}`}>
                    {tx.amount > 0 ? "+" : ""}{tx.amount.toFixed(2)}
                  </div>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}


function MealPlanDialog({ open, onClose, wallet, t }) {
  const [step, setStep] = useState("idle");
  const [meals, setMeals] = useState([]);
  const [activePlan, setActivePlan] = useState(null);

  useEffect(() => {
    if (!open || !wallet) return;
    setStep("loading"); setMeals([]); setActivePlan(null);
    api.get(`/canteen/meal-plans?student_id=${wallet.student_id}&active=true`)
      .then((r) => {
        if ((r.data || []).length) {
          setActivePlan(r.data[0]); setStep("active");
        } else {
          api.get(`/canteen/meal-plans/suggest?student_id=${wallet.student_id}&lang=ar`)
            .then((s) => { setMeals(s.data.meals || []); setStep("preview"); })
            .catch((e) => {
              const raw = e.response?.data?.detail;
              const msg = (typeof raw === "string" && raw.toLowerCase().includes("no products"))
                ? t("mealplan_no_products")
                : (raw || t("error"));
              toast.error(msg);
              setStep("idle"); onClose();
            });
        }
      })
      .catch(() => setStep("idle"));
  }, [open, wallet]); // eslint-disable-line

  const total = meals.reduce((s, m) => s + Number(m.price || 0), 0);

  const approve = async () => {
    setStep("saving");
    try {
      await api.post("/canteen/meal-plans", {
        student_id: wallet.student_id, meals, auto_preorder: true,
      });
      toast.success(t("meal_plan_activated"));
      onClose();
    } catch (e) {
      const det = e.response?.data?.detail;
      toast.error(typeof det === "string" ? det : t("error"));
      setStep("preview");
    }
  };

  const regenerate = async () => {
    setStep("loading");
    try {
      const s = await api.get(`/canteen/meal-plans/suggest?student_id=${wallet.student_id}&lang=ar`);
      setMeals(s.data.meals || []); setStep("preview");
    } catch { setStep("preview"); }
  };

  const cancelActive = async () => {
    if (!window.confirm(t("confirm_cancel_meal_plan"))) return;
    try {
      await api.post(`/canteen/meal-plans/${activePlan.id}/cancel`);
      toast.success(t("saved")); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  if (!wallet) return null;
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="meal-plan-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-primary" /> {t("meal_plan_title")} — {wallet.student_name}
          </DialogTitle>
          <DialogDescription>{t("meal_plan_desc")}</DialogDescription>
        </DialogHeader>

        {step === "loading" && (
          <div className="py-12 flex flex-col items-center text-muted-foreground">
            <Loader2 className="w-8 h-8 animate-spin mb-2" />
            <span>{t("ai_choosing_meals")}</span>
          </div>
        )}

        {step === "active" && activePlan && (
          <div className="space-y-3" data-testid="meal-plan-active">
            <div className="text-sm text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-950/20 p-3 rounded-lg">
              {t("plan_already_active")} ({activePlan.week_start})
            </div>
            <ul className="divide-y border rounded-lg">
              {activePlan.meals.map((m) => (
                <li key={m.date} className="p-3 flex items-center justify-between text-sm">
                  <span>{m.date}</span>
                  <span className="flex-1 px-3">{m.product_name_ar}</span>
                  <span className="font-semibold">{(m.price || 0).toFixed(2)} {t("sar_unit")}</span>
                </li>
              ))}
            </ul>
            <div className="flex justify-between items-center pt-2 border-t">
              <span className="font-semibold">{t("total")}: {activePlan.total_estimated.toFixed(2)} {t("sar_unit")}</span>
              <Button variant="destructive" onClick={cancelActive} data-testid="cancel-meal-plan">{t("cancel_plan")}</Button>
            </div>
          </div>
        )}

        {step === "preview" && (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">{t("ai_suggested_meals_for_week")}</p>
            <ul className="divide-y border rounded-lg">
              {meals.map((m) => (
                <li key={m.date} className="p-3 grid grid-cols-12 gap-2 items-start text-sm" data-testid={`meal-day-${m.date}`}>
                  <span className="col-span-3 font-semibold text-primary">{m.date}</span>
                  <div className="col-span-7">
                    <div className="font-medium">{m.product_name_ar}</div>
                    <div className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1">
                      {m.is_healthy && <Check className="w-3 h-3 text-emerald-600" />}
                      {m.reason_ar}
                    </div>
                  </div>
                  <span className="col-span-2 text-end font-semibold">{m.price.toFixed(2)}</span>
                </li>
              ))}
            </ul>
            <div className="flex items-center justify-between pt-2 border-t">
              <div>
                <div className="text-sm text-muted-foreground">{t("total_week_cost")}</div>
                <div className="text-2xl font-bold">{total.toFixed(2)} {t("sar_unit")}</div>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={regenerate} data-testid="meal-plan-regenerate">
                  <Sparkles className="w-4 h-4 me-1" /> {t("regenerate")}
                </Button>
                <Button onClick={approve} disabled={meals.length < 1 || total > (wallet.balance || 0)} data-testid="meal-plan-approve">
                  <Check className="w-4 h-4 me-1" /> {t("approve_and_subscribe")}
                </Button>
              </div>
            </div>
            {total > (wallet.balance || 0) && (
              <div className="text-xs text-destructive">{t("insufficient_balance_for_plan")}</div>
            )}
          </div>
        )}

        {step === "saving" && (
          <div className="py-8 flex flex-col items-center text-muted-foreground">
            <Loader2 className="w-6 h-6 animate-spin mb-2" />
            <span>{t("creating_plan")}</span>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
